import { useEffect, useRef, useState } from 'react';
import ApplicationsMenu from './ApplicationsMenu';
import KnowledgeBaseMenu from './KnowledgeBaseMenu';
import Noticeboard from '../noticeboard/Noticeboard';
import { DOWNLOADS_PATH } from '../../config/downloads';
import logoMark from '../../assets/images/logo-mark.png';
import './Navbar.css';

const SunIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2v2.4M12 19.6V22M2 12h2.4M19.6 12H22M4.9 4.9l1.7 1.7M17.4 17.4l1.7 1.7M19.1 4.9l-1.7 1.7M6.6 17.4l-1.7 1.7" />
  </svg>
);

const MoonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20.5 14.5A8.6 8.6 0 0 1 9.5 3.5a8.6 8.6 0 1 0 11 11Z" />
  </svg>
);

export default function Navbar({ showAltMenu, setShowAltMenu, theme, onToggleTheme, onDownloads, downloadsActive = false, onTerminalx }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 820);
  // Which dropdown is open: 'apps' | 'kb' | null. One at a time — opening
  // Knowledge base closes Applications and vice versa (design note §2.1).
  const [menu, setMenu] = useState(null);
  const appsRef = useRef(null);
  const kbRef = useRef(null);
  const toggleMenu = (name) => setMenu((m) => (m === name ? null : name));
  const closeMenus = () => setMenu(null);

  useEffect(() => {
    if (!menu) return undefined;
    const inside = (ref, t) => ref.current && ref.current.contains(t);
    const away = (e) => { if (!inside(appsRef, e.target) && !inside(kbRef, e.target)) setMenu(null); };
    const esc = (e) => { if (e.key === 'Escape') setMenu(null); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', away);
      document.removeEventListener('keydown', esc);
    };
  }, [menu]);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 820);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const goHome = () => { setShowAltMenu(false); setMobileOpen(false); setMenu(null); };

  const links = (
    <>
      {showAltMenu ? (
        <button type="button" className="rw-nav__item" onClick={goHome}>Home</button>
      ) : (
        <ApplicationsMenu
          menuRef={appsRef}
          open={menu === 'apps'}
          onToggle={() => toggleMenu('apps')}
          onClose={closeMenus}
          triggerAltMenu={() => { setShowAltMenu(true); setMobileOpen(false); setMenu(null); }}
          onTerminalx={onTerminalx ? () => { setMobileOpen(false); setMenu(null); onTerminalx(); } : undefined}
        />
      )}
      <button type="button" className="rw-nav__item" onClick={goHome}>About Us</button>
      {/* A real address, so it also works opened in a new tab or with no handler. */}
      <a
        className={'rw-nav__item' + (downloadsActive ? ' is-active' : '')}
        href={DOWNLOADS_PATH}
        aria-current={downloadsActive ? 'page' : undefined}
        onClick={(e) => { if (onDownloads) { e.preventDefault(); setMobileOpen(false); setMenu(null); onDownloads(); } }}
      >
        Downloads
      </a>
      {/* Home: a dropdown of guides per application. Product page: a plain link. */}
      <KnowledgeBaseMenu
        mode={showAltMenu ? 'link' : 'menu'}
        menuRef={kbRef}
        open={menu === 'kb'}
        onToggle={() => toggleMenu('kb')}
        onClose={closeMenus}
      />
    </>
  );

  return (
    <header className="rw-nav">
      <div className="rw-nav__inner">
        <button type="button" className="rw-nav__brand" onClick={goHome}>
          <img src={logoMark} alt="Riftworks" />
          <span>RIFTWORKS</span>
        </button>

        {isMobile ? (
          <div className="rw-nav__right">
            {/* The landing page reads the suite feed; the Kuestion product page
                reads Kuestion's. Both acknowledge under the suite key. */}
            <Noticeboard surface={showAltMenu ? 'kuestion' : 'suite'} store="rw" size={36} />
            <button
              type="button"
              className="rw-nav__icon"
              onClick={onToggleTheme}
              title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
            <button
              type="button"
              className={'rw-nav__burger' + (mobileOpen ? ' is-open' : '')}
              aria-label="Menu"
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((o) => !o)}
            >
              <span />
            </button>

            {mobileOpen && <div className="rw-nav__mobile">{links}</div>}
          </div>
        ) : (
          <nav className="rw-nav__right">
            {links}
            {/* The landing page reads the suite feed; the Kuestion product page
                reads Kuestion's. Both acknowledge under the suite key. */}
            <Noticeboard surface={showAltMenu ? 'kuestion' : 'suite'} store="rw" size={36} />
            <button
              type="button"
              className="rw-nav__icon"
              onClick={onToggleTheme}
              title={theme === 'dark' ? 'Switch to light' : 'Switch to dark'}
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
            </button>
          </nav>
        )}
      </div>
    </header>
  );
}
